import React, { useContext, useEffect, useState, useCallback } from "react";
import api from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import { Box, Heading, Flex, Button, useDisclosure } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import ErrorMessage from "../components/onlinemenu/ErrorMessage";
import LoadingSpinner from "../components/onlinemenu/LoadingSpinner"
import DeactivateModal from "../components/onlinemenu/DeactivateModal";
import EditModal from "../components/onlinemenu/EditModal"
import MenuTabs from "../components/onlinemenu/MenuTabs"

const OnlineMenu = () => {
    const { currLocation } = useContext(AuthContext);
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeactivateOpen,
        onOpen: onDeactivateOpen,
        onClose: onDeactivateClose,
    } = useDisclosure();
    const [editData, setEditData] = useState(null);
    const [editType, setEditType] = useState(""); 
    const [deactivateData, setDeactivateData] = useState(null);

    const fetchMenus = useCallback(async () => {
        if (!currLocation) return;
        try {
            setLoading(true);
            const response = await api.get(`/menus/location/${currLocation}`);
            setMenus(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load menus.");
        } finally {
            setLoading(false);
        }
    }, [currLocation]); 

    useEffect(() => {
        if (!currLocation) return;
        fetchMenus();
    }, [currLocation, fetchMenus]);

    const openEditModal = useCallback((type, data) => {
        setEditData(data);
        setEditType(type);
        onOpen();
    }, [onOpen]);

    const openDeactivateModal = useCallback((type, data) => {
        setDeactivateData({ type, data });
        onDeactivateOpen();
    }, [onDeactivateOpen]);

    const handleSave = async (formData) => {
        try {
            let apiEndpoint = "";
            let method = formData.id !== undefined && formData.id !== 0 ? "put" : "post";
            let editingData = formData.id !== undefined ? formData.id : 0;

            if (editType === "section") {
                apiEndpoint = editingData ? `menus/sections/${editingData}/location/${currLocation}` : `menus/sections/location/${currLocation}`;
            } else if (editType === "menu") {
                apiEndpoint = editingData ? `menus/${editingData}/location/${currLocation}` : `menus/location/${currLocation}`;
            } else if (editType === "item") {
                apiEndpoint = editingData ? `menus/items/${editingData}/location/${currLocation}` : `menus/items/location/${currLocation}`;
            }

            const response = await api[method](apiEndpoint, formData);

            if (editType === "menu" && editingData) {
                setMenus((prevMenus) =>
                    prevMenus.map((menu) =>
                        menu.id === editingData ? { ...menu, ...formData, ...response.data } : menu
                    )
                );
            } else if (editType === "menu" && !editingData) {
                const savedMenu = { isActive: true, ...formData, ...response.data };
                setMenus((prevMenus) => [...prevMenus, { ...savedMenu, sections: savedMenu.sections || [] }]);
            } else if (editType === "item" && editingData) {
                const savedItem = { ...formData, ...response.data };
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => ({
                        ...menu,
                        sections: menu.sections.map((section) => {
                            if (!section.items.some((item) => item.id === editingData)) return section;
                            return {
                                ...section,
                                items: section.items.map((item) => (item.id === editingData ? savedItem : item)),
                            };
                        }),
                    }))
                );
            } else if (editType === "item" && !editingData) {
                const savedItem = { isActive: true, ...formData, ...response.data };
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => ({
                        ...menu,
                        sections: menu.sections.map((section) =>
                            section.id === savedItem.sectionId
                                ? { ...section, items: [...section.items, savedItem] }
                                : section
                        ),
                    }))
                );
            } else if (editType === "section" && editingData) {
                const savedSection = { ...formData, ...response.data };
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => {
                        if (!menu.sections.some((section) => section.id === editingData)) return menu;
                        return {
                            ...menu,
                            sections: menu.sections.map((section) =>
                                section.id === editingData
                                    ? { ...section, ...savedSection, items: section.items }
                                    : section
                            ),
                        };
                    })
                );
            } else if (editType === "section" && !editingData) {
                const savedSection = { isActive: true, ...formData, ...response.data };
                setMenus((prevMenus) =>
                    prevMenus.map((menu) =>
                        menu.id === savedSection.menuId
                            ? { ...menu, sections: [...menu.sections, { ...savedSection, items: savedSection.items || [] }] }
                            : menu
                    )
                );
            } else {
                await fetchMenus();
            }

            setEditData(null);
            onClose();
        } catch (error) {
            console.error("Failed to save:", error);
        }
    };

    const handleDeactivate = async () => {
        try {
            const { type, data } = deactivateData || {};
            const fallbackIsActive = !data.isActive;

            if (type === "menu") {
                const response = await api.put(`/menus/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => (menu.id === data.id ? { ...menu, isActive } : menu))
                );
            } else if (type === "section") {
                const response = await api.put(`menus/sections/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => ({
                        ...menu,
                        sections: menu.sections.map((section) =>
                            section.id === data.id ? { ...section, isActive } : section
                        ),
                    }))
                );
            } else if (type === "item") {
                const response = await api.put(`menus/items/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setMenus((prevMenus) =>
                    prevMenus.map((menu) => ({
                        ...menu,
                        sections: menu.sections.map((section) => ({
                            ...section,
                            items: section.items.map((item) =>
                                item.id === data.id ? { ...item, isActive } : item
                            ),
                        })),
                    }))
                );
            }

            setDeactivateData(null);
            onDeactivateClose();
        } catch (error) {
            console.error("Failed to update active state:", error);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box
            as="main"
            pt={{ base: "24px", md: "20px" }}
            pl={{ base: "0px", md: "240px" }}
            pr={{ base: "0px", md: "30px" }}
            pb="20px"
            minH="100vh"
        >
            <Flex justify="space-between" align="center" mb={6} px={4} pt={4}>
                <Heading color="brand.700" size="lg">Online jelovnik</Heading>
                <Button
                    leftIcon={<FaPlus />}
                    bg="brand.600"
                    color="white"
                    borderRadius="full"
                    onClick={() => openEditModal("menu", { name: "", header: "", description: "" })}
                    _hover={{ bg: "brand.700" }}
                >
                    Dodaj
                </Button>
            </Flex>

            <MenuTabs menus={menus} onEdit={openEditModal} onDeactivate={openDeactivateModal} />

            <EditModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={handleSave}
                editData={editData}
                editType={editType}
            />

            <DeactivateModal
                isOpen={isDeactivateOpen}
                onClose={onDeactivateClose}
                onConfirm={handleDeactivate}
                data={deactivateData}
            />
        </Box>
    );
};

export default OnlineMenu;